import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaRegEdit, FaRegTrashAlt } from 'react-icons/fa';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import AddGroupModal from './addGroup';
import { addGroup, deleteGroups, updateGroup } from '../../../../services/commonapi/commonApi';
import moment from 'moment';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGroups, resetGroupsState } from '../../../../redux/slices/groupsSlice';


const ViewGroups = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(10)
  const [page, setPage] = useState(0);
  const [type, setType] = useState('add');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({});
  const dispatch = useDispatch();
  const toast = useRef(null);
  const accessToken = JSON.parse(localStorage.getItem('access_token'));
  const { groupsData, groupLoading } = useSelector((state) => state.groups);

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchGroups(accessToken));
    }
    return () => {
      dispatch(resetGroupsState());
    };
  }, [dispatch, accessToken]);

  const openConfirmDialog = (config) => {
    setDialogConfig(config);
    setIsDialogOpen(true);
  };

  const confirmDelete = (user) => {
    openConfirmDialog({
      title: "Confirm Delete",
      message: `Are you sure you want to delete this group?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      cancelClass: "bg-primary hover:border-white text-gray-300 font-bold py-2 px-4 rounded border border-[rgb(161,147,147)]",
      confirmClass: "bg-secondary hover:bg-gray-400 font-bold py-2 px-4 rounded ml-4 text-primary border",
      onConfirm: () => handleDelete(user),
    });
  };


  const handleDelete = async (user) => {
    try {
      const accessToken = JSON.parse(localStorage.getItem('access_token'));
      const response = await deleteGroups(accessToken, user?.id);
      if (response) {
        dispatch(fetchGroups(accessToken));
        toast.current.show({ className: "bg-green-300 text-white", severity: 'success', summary: 'Success', detail: 'Group deleted successfully', life: 3000 });
      }
    } catch (err) {
      toast.current.show({ className: "bg-yellow-700 text-white", severity: 'warn', summary: 'Warning', detail: 'Failed to delete group', life: 3000 });
    }
  };

  const dateTemplate = (rowData) => {
    return rowData.created_at ? moment(rowData.created_at).format('DD-MM-YYYY') : "no data";
  };

  const actionBodyTemplate = (rowData) => {
    const iconClass = rowData?.is_default ? "text-gray-500 cursor-not-allowed" : "cursor-pointer";

    return (
      <div className="flex gap-2">
        <button disabled={rowData?.is_default} className="focus:outline-none text-secondary">
          <FaRegEdit
            className={iconClass}
            onClick={() => {
              if (!rowData?.is_default) {
                setIsModalOpen(true);
                setType("edit");
                setSelectedGroup(rowData);
              }
            }}
          />
        </button>
        <button disabled={rowData?.is_default} className="focus:outline-none text-expense">
          <FaRegTrashAlt
            className={iconClass}
            onClick={() => {
              if (!rowData?.is_default) {
                confirmDelete(rowData);
              }
            }}
          />
        </button>
      </div>
    );
  };


  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  const handleAddGroupClick = () => {
    setType("add")
    setIsModalOpen(true);
  };

  const handleSubmit = async ({ name, status, onClose, setError, setLoading }) => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    try {

      const response = type === "add" ? await addGroup(name, status, accessToken) : await updateGroup(name, status, selectedGroup.id, accessToken);

      if (response.data.status === true) {
        dispatch(fetchGroups(accessToken));
        toast.current.show({
          className: 'bg-green-300 text-white',
          severity: 'success',
          summary: 'Success',
          detail: response.data.message,
          life: 3000,
        });

        onClose();
      } else {
        toast.current.show({
          className: 'bg-yellow-700 text-white',
          severity: 'warn',
          summary: 'Warning',
          detail: response.data.message || 'Failed to add group',
          life: 3000,
        });
      }
    } catch (error) {
      toast.current.show({
        className: 'bg-red-700 text-white',
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'An unexpected error occurred',
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        {...dialogConfig}
      />
      <div className="p-4">

        <div className="flex justify-end mb-6 flex-col md:flex-row">
          <div className='flex justify-end'>
            <button
              type="submit"
              className="ml-6 inline-flex justify-center px-4 py-2 items-center gap-1 text-sm font-medium text-primary bg-secondary rounded-md hover:bg-gray-100 focus:outline-none"
              onClick={handleAddGroupClick}
            >
              <FaPlus /> Add Group
            </button>
          </div>
        </div>
        <DataTable
          value={groupsData}
          stripedRows
          className="usertable"
          paginator
          rows={limit}
          totalRecords={groupsData.length}
          first={page * limit}
          onPage={(e) => {
            setPage(e.page);
            setLimit(e.rows);
          }}
          loading={loading || groupLoading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column field="name" header="Name" sortable />
          <Column field="status" header="Status" body={(data) => data.status === true ? "Active" : "Inactive"} />
          <Column field="created_at" header="Created Date" body={dateTemplate} />
          <Column field="Action" header="Action" body={actionBodyTemplate} />

        </DataTable>
        {isModalOpen && (
          <AddGroupModal
            type={type}
            selectedGroup={selectedGroup}
            onClose={handleModalClose}
            handleSubmit={handleSubmit}
          />
        )}
      </div>
    </>
  );
};

export default ViewGroups;
